<script lang="ts" setup>
import {
  PopoverButton,
  PopoverOverlay,
  PopoverPanel,
  TransitionChild,
  TransitionRoot,
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from '@headlessui/vue'
import { BellIcon, XMarkIcon } from '@heroicons/vue/24/outline'
import { Link as InertiaLink, router } from '@inertiajs/vue3'
import { userData as user } from '@/Support/Users/userData'
import { userMenu } from '@/Support/Users/userMenu'
import { mainMenu } from '@/Support/Menus/mainMenu'
import { logout } from '@/Support/Routing/Functions/logout'
import { ChevronRightIcon } from '@heroicons/vue/20/solid'
import SpatiumLogo from '@/Components/Shared/Logos/SpatiumLogo.vue'
import { routing } from '@/Support/Routing/Functions/routing'
import NotificationsView from '@/Layouts/App/Components/Desktop/NotificationsView.vue'
import DarkModeMenu from '@/Layouts/App/Components/Desktop/DarkModeMenu.vue'
import Teams from '@/Layouts/App/Components/Teams.vue'

defineProps({
  open: Boolean,
})

const navigate = (route: string, close: unknown) => {
  close()
  router.get(route)
}
</script>

<template>
  <TransitionRoot :show="open" as="template">
    <div class="lg:hidden">
      <TransitionChild
        as="template"
        enter="duration-150 ease-out"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="duration-150 ease-in"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <PopoverOverlay
          class="z-20 fixed inset-0 bg-black/25 dark:bg-neutral-600/75"
        />
      </TransitionChild>

      <TransitionChild
        as="template"
        enter="duration-150 ease-out"
        enter-from="opacity-0 scale-95"
        enter-to="opacity-100 scale-100"
        leave="duration-150 ease-in"
        leave-from="opacity-100 scale-100"
        leave-to="opacity-0 scale-95"
      >
        <PopoverPanel
          v-slot="{ close }"
          class="z-30 absolute top-0 inset-x-0 max-w-3xl mx-auto w-full p-2 transition transform origin-top"
          focus
        >
          <div
            class="rounded-lg shadow-lg ring-1 ring-black/5 backdrop-blur-xs bg-white/75 dark:bg-neutral-950/75 divide-y divide-neutral-200"
          >
            <div class="pt-3 pb-2">
              <div class="flex items-center justify-between px-4">
                <div class="w-36" tabindex="">
                  <SpatiumLogo :route="routing('dashboard')" @click="close" />
                </div>
                <div class="-mr-2">
                  <PopoverButton
                    class="bg-white dark:bg-neutral-700 rounded-md p-2 inline-flex items-center justify-center text-neutral-400 dark:text-neutral-300 hover:text-neutral-500 dark:hover:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-800 focus:outline-hidden focus:ring-2 focus:ring-inset focus:ring-primary-500"
                  >
                    <span class="sr-only">{{ __('Close menu') }}</span>
                    <XMarkIcon aria-hidden="true" class="h-6 w-6" />
                  </PopoverButton>
                </div>
              </div>
              <ul class="mt-3 px-2 space-y-1">
                <li v-for="item in mainMenu" :key="item.name">
                  <a
                    v-if="!item.content"
                    class="block rounded-md p-2 text-base text-neutral-900 dark:text-neutral-200 font-medium"
                    :class="{
                      'bg-secondary-50 dark:bg-secondary-950': item.current,
                    }"
                    @click="navigate(item.url, close)"
                  >
                    {{ item.name }}
                  </a>
                  <Disclosure v-else v-slot="{ open }" as="div">
                    <DisclosureButton
                      :class="[
                        item.current
                          ? 'bg-neutral-50 dark:bg-secondary-950'
                          : 'hover:bg-neutral-50 dark:hover:bg-neutral-800',
                        'flex items-center w-full text-left rounded-md p-2 gap-x-3 text-base font-medium leading-6 text-neutral-900 dark:text-neutral-200',
                      ]"
                    >
                      {{ item.name }}
                      <ChevronRightIcon
                        :class="[
                          open
                            ? 'rotate-90 text-neutral-500'
                            : 'text-neutral-400',
                          'ml-auto h-5 w-5 shrink-0',
                        ]"
                        aria-hidden="true"
                      />
                    </DisclosureButton>
                    <DisclosurePanel as="ul" class="mt-1 px-2">
                      <div v-for="subItem in item.content" :key="subItem.name">
                        <DisclosureButton
                          as="a"
                          :class="[
                            subItem.current
                              ? 'bg-secondary-50 dark:bg-secondary-950'
                              : 'hover:bg-neutral-50 dark:hover:bg-neutral-800',
                            'block rounded-md py-2  pl-4 text-sm leading-6 text-neutral-700 dark:text-neutral-300',
                          ]"
                          @click="navigate(subItem.url, close)"
                        >
                          {{ subItem.name }}
                        </DisclosureButton>
                      </div>
                    </DisclosurePanel>
                  </Disclosure>
                </li>
              </ul>
            </div>

            <div class="pt-4 pb-2">
              <Teams @selected="close" />
            </div>
            <div class="pt-4 pb-2">
              <div class="flex items-center px-5">
                <div class="shrink-0">
                  <img
                    :src="user.profilePhotoUrl"
                    alt=""
                    class="h-10 w-10 rounded-full object-cover"
                  />
                </div>
                <div class="ml-3 min-w-0 flex-1">
                  <div
                    class="text-base font-medium text-neutral-800 dark:text-neutral-200 truncate"
                  >
                    {{ user.name }}
                  </div>
                  <div
                    class="text-sm font-medium text-neutral-500 dark:text-neutral-400 truncate"
                  >
                    {{ user.email }}
                  </div>
                </div>
                <DarkModeMenu />
                <NotificationsView @close="close" />
              </div>
              <div class="mt-3 px-2 space-y-1">
                <a
                  v-for="item in userMenu"
                  :key="item.name"
                  class="block rounded-md px-3 py-2 text-base text-neutral-900 dark:text-neutral-200 font-medium hover:bg-neutral-100 dark:hover:bg-neutral-800 hover:text-neutral-800 dark:hover:bg-neutral-300"
                  @click="navigate(item.url, close)"
                >
                  {{ item.name }}
                </a>
                <a
                  class="block rounded-md px-3 py-2 text-base text-neutral-900 dark:text-neutral-200 font-medium hover:bg-neutral-100 dark:hover:bg-neutral-800 hover:text-neutral-800 dark:hover:bg-neutral-300"
                  @click="logout"
                >
                  {{ __('Log out') }}
                </a>
              </div>
            </div>
          </div>
        </PopoverPanel>
      </TransitionChild>
    </div>
  </TransitionRoot>
</template>
